import moment from "moment-timezone";
import 'moment/locale/es';

export const getDateInMiliseconds = (date: string) => {
    return moment(date).valueOf();
}

export const convertToUTC = (datetimeStr: string): string => {
    const localTimeZone = moment.tz.guess();
    const local = moment.tz(datetimeStr, 'YYYY-MM-DD HH:mm', localTimeZone);

    return local.utc().format('YYYY-MM-DD HH:mm:ss');
}

export const formatWithoutSeconds = (dateTime: string) => {
    return moment(dateTime).format('YYYY-MM-DD HH:mm');
}

export const getOldestDateFromArray = (dates: string[], withoutSecondsFormat?: boolean) => {
    const sortDates = dates.sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
    const format = withoutSecondsFormat ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD HH:mm:ss';

    return moment(sortDates[0]).format(format);
}

export const getRecentDateFromArray = (dates: string[], withoutSecondsFormat?: boolean) => {
    const sortDates = dates.sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
    const format = withoutSecondsFormat ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD HH:mm:ss';

    return moment(sortDates[sortDates.length - 1]).format(format);
}

export const sumTimes = (times: string[]): string => {
    const totalDuration = moment.duration(0);

    times.forEach(time => {
        const [hours, minutes, seconds] = time.split(":").map(Number);
        totalDuration.add(moment.duration({ hours, minutes, seconds }));
    });

    const totalHours = Math.floor(totalDuration.asHours()).toString().padStart(2, "0");
    const totalMinutes = totalDuration.minutes().toString().padStart(2, "0");
    const totalSeconds = totalDuration.seconds().toString().padStart(2, "0");

    return `${totalHours}:${totalMinutes}:${totalSeconds}`;
}

export const subtractTimes = (times: string[]): string => {
    if (times.length === 0) return "00:00:00";

    const [baseHours, baseMinutes, baseSeconds] = times[0].split(":").map(Number);
    const totalDuration = moment.duration({
        hours: baseHours,
        minutes: baseMinutes,
        seconds: baseSeconds,
    });

    for (let i = 1; i < times.length; i++) {
        const [h, m, s] = times[i].split(":").map(Number);
        const currentDuration = moment.duration({ hours: h, minutes: m, seconds: s });
        totalDuration.subtract(currentDuration);
    }

    const milliseconds = totalDuration.asMilliseconds();

    const finalDuration = moment.duration(Math.abs(milliseconds));
    const totalHours = Math.floor(finalDuration.asHours()).toString().padStart(2, "0");
    const totalMinutes = finalDuration.minutes().toString().padStart(2, "0");
    const totalSeconds = finalDuration.seconds().toString().padStart(2, "0");

    return `${milliseconds < 0 ? "-" : ""}${totalHours}:${totalMinutes}:${totalSeconds}`;
};

export const getTimeDifference = (start: string, end: string): string => {
    moment.locale("es");
    const startTime = moment(start);
    const endTime = moment(end);
    const duration = moment.duration(endTime.diff(startTime));

    const months = duration.months();
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    let result = "";

    if (months > 0) result += `${months} mes${months > 1 ? "es" : ""} `;
    if (days > 0) result += `${days} día${days > 1 ? "s" : ""} `;

    result += `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

    return result.trim();
}

export const getMinutesDifference = (date1: string | Date, date2: string | Date): number => {
    const momentDate1 = moment(date1);
    const momentDate2 = moment(date2);

    return momentDate2.diff(momentDate1, 'minutes');
}

export const fetchTimeZones = () => {
    return moment.tz.names();
}

export const getEndTime = (isDateTime = false) => {
    const format = isDateTime ? 'YYYY-MM-DD HH:mm:ss' : 'HH:mm:ss';
    return moment().endOf('day').format(format);
}

export const getTimeFromDateTime = (dateTime: string) => {
    return dateTime.split(' ')[1];
}

export const getTodayStartTimeUTC = () => {
    const browserTimeZone = moment.tz.guess();
    const startTime = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');

    const dateInTimezone = moment.tz(startTime, browserTimeZone);
    const result = dateInTimezone.clone().utc().format('YYYY-MM-DD HH:mm:ss');
    return result;
}

export const getTodayEndTimeUTC = () => {
    const browserTimeZone = moment.tz.guess();
    const endTime = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');

    const dateInTimezone = moment.tz(endTime, browserTimeZone);
    const result = dateInTimezone.clone().utc().format('YYYY-MM-DD HH:mm:ss');

    return result;
}

export const formatSpanishDateTime = (date: string | Date) => {
    const browserTimeZone = moment.tz.guess();
    return moment.utc(date).locale('es').tz(browserTimeZone).format('DD [de] MMMM [de] YYYY, HH:mm:ss');
}

export const formatTimePassedSpanish = (date: string | Date) => {
    const browserTimeZone = moment.tz.guess();
    return moment.utc(date).locale('es').tz(browserTimeZone).locale("es").fromNow();
}

export const getCurrentDate = () => {
    return moment().format('YYYY-MM-DD');
}

export const getCurrentDateTime = () => {
    return moment().format('YYYY-MM-DD HH:mm:ss');
}

export const getCurrentUTCDateTime = () => {
    return moment().utc().format('YYYY-MM-DD HH:mm:ss');
}

export const getStartOfWeekUTC = (): string => {
    return moment().startOf('isoWeek').utc().format('YYYY-MM-DD HH:mm:ss');
}

export const getEndOfWeekUTC = (): string => {
    return moment().endOf('isoWeek').utc().format('YYYY-MM-DD HH:mm:ss');
}

export const getCurrentTimeZone = () => {
    return moment.tz.guess();
}

export const formatGenericDate = (date: string): string => {
    return moment(date).format('YYYY-MM-DD');
}

export const formatGenericDateTime = (date: string): string => {
    return moment(date).format('YYYY-MM-DD HH:mm');
}

export const formatDateTime = (date: string): string => {
    return moment.utc(date).format('YYYY-MM-DD HH:mm:ss');
}

export const getCurrentTime = () => {
    return moment().format('HH:mm:ss');
}

export const getMidnightDateTime = () => {
    return moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');
}

export const getMidnightTime = () => {
    return moment().startOf('day').format('HH:mm:ss');
}

export const formatGenericTime = (date: string): string => {
    return moment(date).format('HH:mm:ss');
}

export const parseToDate = (datetime: string): string => {
    return moment(datetime).format('YYYY-MM-DD');
}

export const parseToTime = (datetime: string): string => {
    return moment(datetime).format('HH:mm:ss');
}

export const parseLowerLimit = (date: string) => {
    const timezone = moment.tz.guess();
    const nowInTimezone = moment.tz(date, timezone);
    const lowerLimit = nowInTimezone.startOf('day');
    const maxNextDateInUTC = lowerLimit.utc();

    return maxNextDateInUTC.format('YYYY-MM-DD HH:mm:ss');
}

export const parseUpperLimit = (date: string) => {
    const timezone = moment.tz.guess();
    const nowInTimezone = date ? moment.tz(date, timezone) : moment.tz(timezone);
    const maxNextDateInTimezone = nowInTimezone.add(1, 'day').startOf('day');
    const maxNextDateInUTC = maxNextDateInTimezone.utc();

    return maxNextDateInUTC.format('YYYY-MM-DD HH:mm:ss');
}

export const parseToUtcDateTime = (date: string): string => {
    return moment(date).utc().format('YYYY-MM-DD HH:mm:ss');
}

export const parseToUtcHour = (time: string): string => {
    const userTimezone = moment.tz.guess();
    const localTime = moment.tz(time, "HH:mm:ss", userTimezone);
    const utcTime = localTime.utc();

    return utcTime.format("HH:mm:ss");
}

export const parseToUtcDate = (date: string): string => {
    return moment.utc(date).format('YYYY-MM-DD');
}

export const parseToLocalDateFromUtc = (date: string): string => {
    const browserTimeZone = moment.tz.guess();
    const dateFormat = moment.utc(date).tz(browserTimeZone);
    const result = dateFormat.format('YYYY-MM-DD');

    return result;
}

export const parseToLocalDateTimeFromUtc = (dateTime: string): string => {
    const browserTimeZone = moment.tz.guess();
    const date = moment.utc(dateTime).tz(browserTimeZone);
    const result = date.format('YYYY-MM-DD HH:mm:ss');

    return result;
}

export const parseToLocalTimeFromUtcDateTime = (dateTime: string): string => {
    const browserTimeZone = moment.tz.guess();
    const date = moment.utc(dateTime).tz(browserTimeZone);
    const result = date.format('HH:mm:ss');

    return result;
}

export const parseToLocalTimeFromUtc = (time: string): string => {
    const browserTimeZone = moment.tz.guess();
    const date = getCurrentDate();
    const datetime = date + " " + time;

    const dateTz = moment.utc(datetime).tz(browserTimeZone);
    const result = dateTz.format('HH:mm:ss');
    return result;
}

export const parseToUtcTime = (time: string): number => {
    const timezoneOffsetMinutes = moment().utcOffset();
    const timezoneOffsetSeconds = timezoneOffsetMinutes * 60;

    const timeMoment = moment(time, "HH:mm:ss");
    const totalSeconds = timeMoment.diff(moment().startOf('day'), 'seconds');
    const result = (timezoneOffsetSeconds * -1) + totalSeconds;

    return result;
}

export const parseToTimeFromTimeSeconds = (timeOnSeconds: number): string => {
    const timezoneOffsetMinutes = moment().utcOffset();
    const timezoneOffsetSeconds = (timezoneOffsetMinutes * 60);

    const totalSeconds = (timezoneOffsetSeconds < 0) ?
        timeOnSeconds - (timezoneOffsetSeconds * (-1)) : timeOnSeconds + timezoneOffsetSeconds;

    const duration = moment.duration(totalSeconds, 'seconds');
    const formattedTime = moment.utc(duration.asMilliseconds()).format('HH:mm:ss');

    return formattedTime;
}

export const parseToUTCDateTime = (date: string, time: string) => {
    const browserTimeZone = moment.tz.guess();
    const datetime = date + " " + time;

    const dateInTimezone = moment.tz(datetime, browserTimeZone);
    const result = dateInTimezone.clone().utc().format('YYYY-MM-DD HH:mm:ss');

    return result;
}

export const substractHoursCurrentTime = (hour: number, isDateTime = false): string => {
    const format = isDateTime ? 'YYYY-MM-DD HH:mm:ss' : 'HH:mm:ss';
    const result = moment().subtract(hour, 'hours').format(format);

    return result;
}

export const parseToTimeFromSeconds = (seconds: number): string => {
    const duration = moment.duration(seconds, 'seconds');
    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const secs = duration.seconds().toString().padStart(2, '0');

    return `${hours}:${minutes}:${secs}`;
}

export const isDateOnRange = (date: string, startDate: string, endDate: string) => {
    const dateToAnalize = moment(date);
    const startRange = moment(startDate);
    const endRange = moment(endDate);

    return dateToAnalize.isSameOrAfter(startRange) && dateToAnalize.isSameOrBefore(endRange);
}

export const addDaysToDate = (date: string, days: number, isDateTime?: boolean) => {
    const format = isDateTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
    return moment(date).add(days, 'day').format(format);
}

export const addYearsToDate = (date: string, years: number, isDateTime?: boolean) => {
    const format = isDateTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
    return moment(date).add(years, 'years').format(format);
}

export const getCurrentMonthYear = () => {
    return moment().format('YYYY/MM');
}

export const parseMonthYearFormat = (date: string) => {
    return moment(date).format('YYYY/MM');
}

export const getDatesBetweeen = (startDate: string, endDate: string) => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (!start.isValid() || !end.isValid()) {
        throw new Error('Invalid date format');
    }

    if (end.isBefore(start)) {
        throw new Error('End date must be after start date');
    }

    const dates: string[] = [];
    const current = start.clone();

    while (current.isSameOrBefore(end)) {
        dates.push(current.format('YYYY-MM-DD'));
        current.add(1, 'day');
    }

    return dates;
}

export const getTimeMomentDifference = (startDate: Date | string, endDate: Date | string): string => {
    const start = moment(startDate);
    const end = moment(endDate);

    const duration = moment.duration(end.diff(start));

    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const seconds = duration.seconds().toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
}


export const areDatesSame = (first_date: string, second_date: string, isDateTime?: boolean): boolean => {
    const format = isDateTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
    const f_date = moment(first_date).format(format);
    const s_date = moment(second_date).format(format);
    return f_date === s_date;
}

export const isNextDay = (date1: string | Date, date2: string | Date): boolean => {
    const d1 = moment(date1).startOf('day');
    const d2 = moment(date2).startOf('day');
  
    return d1.add(1, 'day').isSame(d2);
} 

export const validateDate = (input: string): boolean => {
    const dateTimeRegex = /^(\d{4})-(\d{2})-(\d{2})$/;

    const match = input.match(dateTimeRegex);

    if (!match) {
        return false; // The input does not match either format
    }

    // Extract parts of the date and time to validate further
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);


    // Validate ranges of month, day, hour, minute, and second
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return false;
    }

    return true;
}


export const validateTime = (input: string): boolean => {
    const dateTimeRegex = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
    const match = input.match(dateTimeRegex);

    if (!match) {
        return false;
    }

    const hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    const second = parseInt(match[3], 10);

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return false;
    }

    if (second !== null && (second < 0 || second > 59)) {
        return false;
    }

    return true;
}



export const validateDateTime = (input: string): boolean => {
    const dateTimeRegex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/;

    // Check if the input matches the pattern
    const match = input.match(dateTimeRegex);

    if (!match) {
        return false; // The input does not match either format
    }

    // Extract parts of the date and time to validate further
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const second = match[6] ? parseInt(match[6], 10) : null; // second is optional

    // Validate ranges of month, day, hour, minute, and second
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return false;
    }

    if (second !== null && (second < 0 || second > 59)) {
        return false;
    }

    return true;
}

export const addTimeToDateTime = (dateTime: string, amount: number, unit: moment.DurationInputArg2) => {
    return moment(dateTime).add(amount, unit).format('YYYY-MM-DD HH:mm:ss');
}

export const isDateTimeEarlier = (datetime1: string, datetime2: string): boolean => {
    const parsedTime1 = moment(datetime1, 'YYYY-MM-DD HH:mm:ss');
    const parsedTime2 = moment(datetime2, 'YYYY-MM-DD HH:mm:ss');

    return parsedTime1.isBefore(parsedTime2);
}

export const isTimeEarlier = (time1: string, time2: string): boolean => {
    const parsedTime1 = moment(time1, 'HH:mm:ss');
    const parsedTime2 = moment(time2, 'HH:mm:ss');

    return parsedTime1.isBefore(parsedTime2);
}

export const parseToMinutes = (date: string) => {
    const duration = moment.duration(date);
    const totalMinutes = duration.asMinutes();

    return totalMinutes;
}

export const parseToHours = (date: string) => {
    const duration = moment.duration(date);
    const totalHours = duration.asHours();

    return totalHours;
}

// eslint-disable-next-line
export const areDateTimesEqual = (dateTime1: string, dateTime2: string, format: string = ''): boolean => {
    const moment1 = format ? moment(dateTime1, format) : moment(dateTime1);
    const moment2 = format ? moment(dateTime2, format) : moment(dateTime2);

    return moment1.isSame(moment2);
}

// eslint-disable-next-line
export const getHoursDifference = (dateTime1: string, dateTime2: string, format: string = ''): number => {
    const moment1 = format ? moment(dateTime1, format) : moment(dateTime1);
    const moment2 = format ? moment(dateTime2, format) : moment(dateTime2);

    return moment2.diff(moment1, 'hours', true);
}

export const getHoursFromTime = (timeStr: string): number => {
    const [hoursStr, minutesStr = '0'] = timeStr.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    return hours + minutes / 60;
}

export const getMinutesFromTime = (timeStr: string): number => {
    const duration = moment.duration(timeStr);
    const totalMinutes = duration.asMinutes();

    return totalMinutes;
}